'use client';

import { Transition, TransitionChild } from '@headlessui/react';
import { FocusScope } from '@react-aria/focus';
import { OverlayContainer, usePreventScroll } from '@react-aria/overlays';
import { useDrag } from '@use-gesture/react';
import mergeRefs from 'merge-refs';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMeasure } from 'react-use';

import { cx } from '@/ui/cva';

import { useMobileNavigation } from './contexts';

export function MobileNavigationFlyoutOverlay({
    children,
}: {
    children: ReactNode;
}) {
    const isOpen = useMobileNavigation((s) => s.isOpen);
    const close = useMobileNavigation((s) => s.close);
    const [sizeRef, size] = useMeasure<HTMLDivElement>();
    const nodeRef = useRef<HTMLDivElement>(null);
    const ref = mergeRefs<HTMLDivElement>(sizeRef, nodeRef);

    const [{ isDragging, offset }, setDrag] = useState({
        isDragging: false,
        offset: 0,
    });

    usePreventScroll({
        isDisabled: !isOpen,
    });

    useDrag(
        ({ tap, first, last, swipe: [sx], movement: [mx] }) => {
            if (first) {
                setDrag({
                    isDragging: true,
                    offset: 0,
                });
            } else if (last) {
                setDrag((p) => ({
                    ...p,
                    isDragging: false,
                }));

                if (sx > 0 || mx >= size.width * 0.5) {
                    close();
                } else {
                    setDrag((p) => ({
                        ...p,
                        offset: 0,
                    }));
                }
            } else if (!tap) {
                setDrag({
                    isDragging: true,
                    offset: Math.min(Math.max(mx, 0), size.width),
                });
            }
        },
        {
            axis: 'x',
            target: nodeRef,
            filterTaps: true,
            preventDefault: true,
            eventOptions: {
                passive: false,
            },
        }
    );

    useEffect(() => {
        if (isOpen) {
            setDrag((p) => ({
                ...p,
                offset: 0,
            }));
        }
    }, [isOpen]);

    return (
        <Transition show={isOpen}>
            <OverlayContainer>
                <FocusScope autoFocus contain restoreFocus>
                    <div
                        ref={ref}
                        id="mobile-menu"
                        className="fixed inset-0 z-40 flex min-h-0 touch-none select-none flex-col overflow-hidden"
                    >
                        <TransitionChild>
                            <div
                                className={cx(
                                    'absolute inset-0',
                                    'transition duration-500',
                                    'bg-black/50 backdrop-blur',
                                    'data-[leave]:pointer-events-none',
                                    'data-[closed]:pointer-events-none data-[closed]:bg-black/0 data-[closed]:backdrop-blur-0'
                                )}
                                aria-hidden
                                onClick={close}
                            />
                        </TransitionChild>
                        <TransitionChild>
                            <div
                                className={cx(
                                    'ml-[82px] flex min-h-0 flex-1 flex-col bg-white will-change-transform',
                                    !isDragging
                                        ? 'transition'
                                        : 'transition-none',
                                    'translate-x-[--offset]',
                                    'data-[closed]:translate-x-full'
                                )}
                                style={{
                                    '--offset': `${offset}px`,
                                }}
                            >
                                {children}
                            </div>
                        </TransitionChild>
                    </div>
                </FocusScope>
            </OverlayContainer>
        </Transition>
    );
}
